/**
 * Geometry for the drag-resizable scheduler label panel — the "per-column
 * squeeze budget" (approved design, see the mockup Niv signed off on).
 *
 * One input drives everything: the label column's base (pre-zoom) width in px,
 * which the page writes to the inherited `--gantt-label-col` custom property.
 * As the panel narrows, each column gives up width only as far as its own rule
 * allows — a single smooth motion, no stepped column drops:
 *
 *   - TASK NAME  — the `1fr` remainder; shrinks to at most 70% of its natural
 *                  width (enforced by MIN_W), then the name text truncates.
 *   - PROGRESS   — fixed (`--gantt-col-ppc`); the % is always fully visible.
 *   - ASSIGNEE   — the chip + icon (chrome) never shrink; only the profession
 *                  text squeezes, down to 30% of its natural text width.
 *   - STATUS     — the pill narrows smoothly, blanks its header label, then
 *                  becomes a colored status circle at the minimized end.
 *
 * All constants are BASE px (pre-zoom); the grids re-apply `* --sched-zoom`, so
 * the math is zoom-invariant. Natural values mirror the shipped tokens
 * (`--gantt-label-col: 540`, `--gantt-col-ppc/trade/status: 76/96/100`) so the
 * un-resized panel looks exactly as before — resize only squeezes down from it.
 */

/** `.taskRow` horizontal padding (0 16px), base px. */
const PAD = 32;
/** PROGRESS column (`--gantt-col-ppc`). Natural width fits the "PROGRESS"
 *  header; squeezes down to a floor that still fully shows the widest value
 *  ("100%") — the % is never clipped, but the slack is reclaimed. */
const PROG_FULL = 76;
const PROG_FLOOR = 44;

/** ASSIGNEE: chip padding + trade icon + gap that never squeezes. */
const ASSN_CHROME = 30;
/** Natural assignee text width = trade token (96) minus chrome. */
const ASSN_TXT_FULL = 66;
/** Text floor — 30% of natural (per the rule). */
const ASSN_TXT_FLOOR = Math.round(ASSN_TXT_FULL * 0.3); // 20
const ASSN_FULL = ASSN_CHROME + ASSN_TXT_FULL; // 96 (= --gantt-col-trade)
const ASSN_FLOOR = ASSN_CHROME + ASSN_TXT_FLOOR; // 50

/** STATUS: natural pill width (= --gantt-col-status) → minimized circle cell. */
const STAT_FULL = 100;
/** The row kebab's lane (it sits 6px from the row edge, 24px wide, over the
 *  16px row padding: 6 + 24 − 16). The status cell gives it up while the row
 *  is hovered — `RailCells`' `--kebab-lane` — so the status and the hover
 *  Commit icon re-centre clear of the kebab. */
const KEBAB_LANE = 14;
const STAT_CIRCLE = 30 + KEBAB_LANE;
/** Below this status width the pill collapses to a colored circle. Set high
 *  enough that the pill circles *before* it can crowd the assignee column or a
 *  long label ("In Progress") would wrap — both reported as looking wrong. */
const STATUS_CIRCLE_BELOW = 64;
/** Below this status width the STATUS header label blanks out (before circle). */
const STATUS_HEADER_BLANK_BELOW = 80;

/** TASK NAME natural track width (the `1fr` remainder at full width). */
const NAME_FULL = 236; // 540 - PAD - PROG_FULL - ASSN_FULL - STAT_FULL
const NAME_FLOOR = Math.round(NAME_FULL * 0.7); // 165

/** Natural (default) label width — unchanged from the shipped token. */
export const LABEL_PANEL_FULL = NAME_FULL + PROG_FULL + ASSN_FULL + STAT_FULL + PAD; // 540
/** Fully-squeezed width: every column at its floor. */
export const LABEL_PANEL_MIN = NAME_FLOOR + PROG_FLOOR + ASSN_FLOOR + STAT_CIRCLE + PAD; // 335
/** Upper bound = the natural width; the panel only squeezes down from it, it
 *  never grows past what the column was before the resize handle existed. */
export const LABEL_PANEL_MAX = LABEL_PANEL_FULL; // 540

export const clampLabelPanelWidth = (w: number): number =>
  Math.max(LABEL_PANEL_MIN, Math.min(LABEL_PANEL_MAX, w));

export interface ILabelPanelGeometry {
  /** Clamped base width to write to `--gantt-label-col`. */
  width: number;
  /** PROGRESS column base px → `--gantt-col-ppc`. */
  ppcPx: number;
  /** ASSIGNEE column base px → `--gantt-col-trade`. */
  tradePx: number;
  /** STATUS column base px → `--gantt-col-status`. */
  statusPx: number;
  /** At the minimized end the status pill renders as a colored circle. */
  asStatusCircle: boolean;
  /** The STATUS header label hides once the column starts shrinking hard. */
  showStatusHeader: boolean;
}

/**
 * Derive every column's width + degradation flags from the panel's base width.
 * `t` runs 0 (fully squeezed) → 1 (natural); the ASSIGNEE/STATUS widths
 * interpolate linearly between floor and full, and the NAME `1fr` track absorbs
 * whatever's left (which MIN_W guarantees stays ≥ 70% of natural).
 */
export function computeLabelPanel(rawWidth: number): ILabelPanelGeometry {
  const width = clampLabelPanelWidth(Math.round(rawWidth));
  const t = Math.max(0, Math.min(1, (width - LABEL_PANEL_MIN) / (LABEL_PANEL_FULL - LABEL_PANEL_MIN)));
  const ppcPx = Math.round(PROG_FLOOR + t * (PROG_FULL - PROG_FLOOR));
  const tradePx = Math.round(ASSN_FLOOR + t * (ASSN_FULL - ASSN_FLOOR));
  const statusPx = Math.round(STAT_CIRCLE + t * (STAT_FULL - STAT_CIRCLE));
  return {
    width,
    ppcPx,
    tradePx,
    statusPx,
    asStatusCircle: statusPx < STATUS_CIRCLE_BELOW,
    showStatusHeader: statusPx >= STATUS_HEADER_BLANK_BELOW,
  };
}
